import { API_PATHS } from "../constants/api-paths";
import { COMMON_SSO_MOBILE } from "../constants/sso";
import { createCacheAsync } from "@repo/shared-utils/cache";
import { decryptLocalStorageMobileNo } from "@repo/shared-utils/crypto";
import { type IResponse, encryptedRequest } from "../core/api.client";

export interface IOffers {
  existingLan: string;
  id: string;
  offerId: string;
  icorsOfferType: string;
  icorsAmount: string;
  flag: string;
  icorsProduct: string;
  offerName: string;
  customerName: string;
  segmentId: string;
  icorsOfferNameKey: string;
  icorsOfferNameValue: string;
  skuId: string;
  offerRoi: string;
  offerTenor: string;
  employmentType: string;
  journeyName: string;
  sourcingBranchId: string;
  pqOfferRoiAnnual: string;
  pqOfferIrr: string;
  offerType: string;
  offerPf: string;
  offerProduct: string;
  preApprovedAmount: string;
  preQualifiedAmount: string;
  processSegmentation?: string;
  loanType?: string;
  loanVariant: string;
  policyTenure?: string;
  premiumPayingTerm?: string;
  lifeCover?: string;
  premiumStartingFrom?: string;
  productCode?: string;
  employment?: string;
  displayPriority: number;
  displayName?: string;
}
interface IIcorsApiResponse {
  offers: IOffers[];
}

export interface IOffersPromptData {
  mobile: string;
  id: string;
  icorsOfferType: string;
  icorsDate: string;
  systemModstamp: string;
  amount: string;
  flag: string;
  poName: string;
  pinCode: string;
  product: string;
  offerId: string;
  customerId: string;
  displayName: string;
  twoYearPremium: string;
  threeYearPremium: string;
  oneYearPremium: string;
  partnerName: string;
  offerStatus: string;
  createdDate: string;
  dealId: string;
  lan: string;
  emiAmount: string;
  nextEmiDueDate: string;
  productCategory: string;
  model: string;
  manufacturer: string;
  invoiceNo: string;
  invoiceDate: string;
  invoiceAmount: string;
  serialNo: string;
  insuranceProductSelection: string;
  expiryDate: string;
  offerDisplayMedium: string;
  customerId1: string;
  fddTelebinding: string;
  productMake: string;
  opportunityName: string;
}

export interface IOffersPromptResponse {
  offerList: IOffersPromptData[];
  description: string;
  statusCode: string;
}

const getIcorsApi = async (mobileNo: string): Promise<IResponse<IIcorsApiResponse>> => {
  const response = await encryptedRequest<IIcorsApiResponse>({
    method: "POST",
    url: API_PATHS.icors,
    data: {
      mobileNo,
    },
  });

  // 91 is for no offer cases
  if (![90, 91].includes(response.statusCode)) {
    throw new Error(response.message);
  }

  return response;
};

export const getIcors = async (): Promise<IResponse<IIcorsApiResponse>> => {
  const localStorageMobileNo = localStorage.getItem(COMMON_SSO_MOBILE);
  const mobileNo = await decryptLocalStorageMobileNo(localStorageMobileNo || "");
  const response = await createCacheAsync(`${API_PATHS.icors}-${mobileNo}`, getIcorsApi, mobileNo);
  return response;
};

export const offerAPI = async (icorsProduct: string): Promise<IOffers[]> => {
  const product = icorsProduct?.length ? icorsProduct : "";
  const offerIdentifier = product.toUpperCase().replace(/\s/g, "").split(",");
  const response = await getIcors();
  return (
    response?.data?.offers?.filter((offer: IOffers) =>
      offerIdentifier.includes(String(offer.icorsProduct).toUpperCase().replace(/\s/g, ""))
    ) || []
  );
};

interface IB2cRoiApiRequest {
  mobileNo: string;
  offerId: string;
  processSegmentation: string;
  processType: string;
}

interface ITenorData {
  minTenor: number;
  maxTenor: number;
  minLoanAmount: number;
  maxLoanAmount: number;
  allowedTenor: number[];
}

interface ITenorList {
  selfemp: ITenorData[];
  sal: ITenorData[];
}

interface IB2cRoiApiResponse {
  roi: {
    loanAmtFrom: string;
    loanAmtTo: string;
    value: string;
    extId: string;
    minTenor: string;
    maxTenor: string;
  }[];
  productWiseTenorsList: {
    pname: string;
    minTenor: number;
    maxTenor: number;
    minAmount: number;
    maxAmount: number;
    plist: number[];
  }[];
  ruralTenors: ITenorList;
  urbanTenors: ITenorList;
  plCardTenors: ITenorList;
}

const b2cRoiApi = async (data: IB2cRoiApiRequest): Promise<IResponse<IB2cRoiApiResponse>> => {
  const response = await encryptedRequest<IB2cRoiApiResponse, IB2cRoiApiRequest>({
    method: "POST",
    url: API_PATHS.b2cRoi,
    data,
  });

  if (response.statusCode !== 90) {
    throw new Error(response.message);
  }

  return response;
};

export const getB2cRoi = async (
  offerId: string,
  processSegmentation: string,
  processType: string
): Promise<IResponse<IB2cRoiApiResponse>> => {
  const localStorageMobileNo = localStorage.getItem(COMMON_SSO_MOBILE);
  const mobileNo = await decryptLocalStorageMobileNo(localStorageMobileNo || "");
  const response = await createCacheAsync(
    `${API_PATHS.b2cRoi}-${mobileNo}-${offerId}-${processSegmentation}-${processType}`,
    b2cRoiApi,
    {
      mobileNo,
      offerId,
      processSegmentation,
      processType,
    }
  );
  return response;
};
